import { useState, useEffect, useCallback } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { PartyPopper, Trash2, Users, Coins, ImagePlus, Loader2, ChevronDown, ChevronUp } from "lucide-react";

const creditPreview = (price) => {
  const p = parseFloat(price);
  if (!p || p <= 0) return 0;
  return Math.max(5, Math.round(((p + 2) * 150) / 9.99 / 5) * 5);
};

const EMPTY_FORM = { title: "", description: "", date_time: "", location: "", price: "", capacity: "" };

export default function AdminEvents({ password }) {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [photoFile, setPhotoFile] = useState(null);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const fetchEvents = useCallback(async () => {
    try {
      const r = await api.get(`/admin/events?admin_password=${password}`);
      setEvents(r.data.events);
    } catch (e) {
      console.error("Failed to fetch events:", e);
    }
  }, [password]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      let photo_url = null;
      if (photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        const up = await api.post(`/admin/events/upload-photo?admin_password=${password}`, fd);
        photo_url = up.data.url;
      }
      await api.post(`/admin/events?admin_password=${password}`, {
        title: form.title.trim(),
        description: form.description.trim(),
        date_time: form.date_time,
        location: form.location.trim(),
        price: parseFloat(form.price) || 0,
        capacity: parseInt(form.capacity, 10) || 1,
        photo_url,
      });
      toast.success("Event created — it's live for members now");
      setForm(EMPTY_FORM);
      setPhotoFile(null);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create event");
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = async (ev) => {
    if (!confirm(`Cancel "${ev.title}"? Attendees who paid with credits are refunded automatically; PayPal payments must be refunded manually.`)) return;
    try {
      const r = await api.delete(`/admin/events/${ev.event_id}?admin_password=${password}`);
      toast.success(`Event cancelled — ${r.data.credit_refunds} credit refund(s) issued`);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel event");
    }
  };

  const priceNum = parseFloat(form.price) || 0;

  return (
    <div className="glass-card p-6 mb-6 border border-fuchsia-500/30" data-testid="admin-events-section">
      <div className="flex items-center gap-3 mb-4">
        <PartyPopper className="w-6 h-6 text-fuchsia-400" />
        <h2 className="text-lg font-semibold">Events — Create &amp; Manage</h2>
      </div>

      <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="admin-event-form">
        <Input placeholder="Event title" className="bg-white/5 border-white/10" value={form.title} onChange={set("title")} required data-testid="event-title-input" />
        <Input placeholder="Location (venue, city)" className="bg-white/5 border-white/10" value={form.location} onChange={set("location")} required data-testid="event-location-input" />
        <Input type="datetime-local" className="bg-white/5 border-white/10" value={form.date_time} onChange={set("date_time")} required data-testid="event-datetime-input" />
        <div className="grid grid-cols-2 gap-3">
          <Input type="number" min="0" step="0.01" placeholder="Price $ (0 = free)" className="bg-white/5 border-white/10" value={form.price} onChange={set("price")} required data-testid="event-price-input" />
          <Input type="number" min="1" placeholder="Capacity" className="bg-white/5 border-white/10" value={form.capacity} onChange={set("capacity")} required data-testid="event-capacity-input" />
        </div>
        <Textarea placeholder="Description — what to expect, dress code, what's included…" className="bg-white/5 border-white/10 md:col-span-2 min-h-[80px]" value={form.description} onChange={set("description")} data-testid="event-description-input" />
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer md:col-span-2">
          <ImagePlus className="w-4 h-4" />
          <span>{photoFile ? photoFile.name : "Event photo (optional)"}</span>
          <input type="file" accept="image/*" className="hidden" onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} data-testid="event-photo-input" />
        </label>
        <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-xs text-muted-foreground flex-1" data-testid="event-price-preview">
            {priceNum > 0 ? (
              <>Members pay <span className="text-white font-semibold">${(priceNum + 2).toFixed(2)}</span> via PayPal (incl. $2 fee) or <span className="text-amber-400 font-semibold">{creditPreview(form.price)} credits</span></>
            ) : (
              "Free event — members join with one tap"
            )}
          </p>
          <Button type="submit" className="bg-fuchsia-600 hover:bg-fuchsia-700" disabled={creating} data-testid="create-event-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PartyPopper className="w-4 h-4 mr-2" />}
            Create Event
          </Button>
        </div>
      </form>

      {events.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="admin-events-empty">No events yet — create your first one above.</p>
      ) : (
        <div className="space-y-3">
          {events.map((ev) => (
            <div key={ev.event_id} className={`p-4 rounded-lg border ${ev.active ? "bg-white/5 border-white/10" : "bg-red-500/5 border-red-500/20 opacity-70"}`} data-testid={`admin-event-${ev.event_id}`}>
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                {ev.photo_url && <img src={ev.photo_url} alt={ev.title} className="w-16 h-16 rounded-lg object-cover shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium">{ev.title}</p>
                    {!ev.active && <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-500/20 text-red-400">CANCELLED</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {new Date(ev.date_time).toLocaleString()} • {ev.location} • ${ev.price?.toFixed(2)}
                    {ev.price > 0 && <> (+$2 fee = ${ev.total_price?.toFixed(2)} / {ev.credit_price} credits)</>}
                  </p>
                  <p className="text-xs mt-0.5">
                    <Users className="w-3 h-3 inline mr-1 text-fuchsia-400" />
                    <span className="text-fuchsia-400 font-semibold">{ev.attendee_count}/{ev.capacity}</span> attendees
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {ev.attendees?.length > 0 && (
                    <Button size="sm" variant="outline" className="border-white/10" onClick={() => setExpanded(expanded === ev.event_id ? null : ev.event_id)} data-testid={`event-attendees-toggle-${ev.event_id}`}>
                      {expanded === ev.event_id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} Attendees
                    </Button>
                  )}
                  {ev.active && (
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10" onClick={() => handleCancel(ev)} data-testid={`cancel-event-${ev.event_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              {expanded === ev.event_id && (
                <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5" data-testid={`event-attendees-list-${ev.event_id}`}>
                  {ev.attendees.map((a) => (
                    <div key={a.user_id} className="flex items-center justify-between text-sm px-3 py-1.5 rounded-lg bg-white/5">
                      <div className="min-w-0">
                        <span className="font-medium">{a.name || "Member"}</span>
                        <span className="text-xs text-muted-foreground ml-2">{a.email}</span>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${a.paid_via === "paypal" ? "bg-blue-500/15 text-blue-400" : a.paid_via === "credits" ? "bg-amber-500/15 text-amber-400" : "bg-white/10 text-muted-foreground"}`}>
                        {a.paid_via === "paypal" ? `PayPal $${a.amount_usd?.toFixed(2)}` : a.paid_via === "credits" ? <><Coins className="w-3 h-3 inline mr-0.5" />{a.amount_credits}</> : "Free"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
